import React, { useMemo } from 'react';
import { styled } from '@compiled/react';
import { ScreenName } from '@helpcenter/constants';
import { isElementRendererSplitEnabled } from '@helpcenter/feature-flags';
import { di } from 'react-magnetic-di';
import { useFragment } from 'react-relay';
import { graphql } from 'relay-runtime';
import type { AlignmentSettingsType } from 'view/layout-primitives/alignment-settings-context';
import { useAlignmentSettings } from 'view/layout-primitives/alignment-settings-context';
import type { GridItemProps } from '@atlaskit/grid';
import { GridItem } from '@atlaskit/grid';
import { token } from '@atlaskit/tokens';
import { HomePageElementViewRenderer } from '@atlassian/help-center-home-page-element-view-renderer/src/ui/home-page-element-view-renderer';
import { useElementRenderer } from '../../layout-next/controllers';
import { ElementErrorBoundary } from '../element-error-boundary';
import type { subsectionViewRendererFragment$key } from './__generated__/subsectionViewRendererFragment.graphql';

export interface SubsectionProps {
	subsection: subsectionViewRendererFragment$key;
}

type SpanOptions = Extract<NonNullable<GridItemProps['span']>, number | 'none'>;

const SubsectionViewRenderer: React.FC<SubsectionProps> = ({ subsection }) => {
	di(isElementRendererSplitEnabled);
	const [ElementRenderer] = useElementRenderer();
	const subsectionData = useFragment(
		graphql`
			fragment subsectionViewRendererFragment on HelpLayoutSubsection {
				__id
				config {
					span
				}
				elements {
					__id
					# eslint-disable-next-line relay/must-colocate-fragment-spreads
					...homePageElementViewRendererFragment
					# eslint-disable-next-line relay/must-colocate-fragment-spreads
					...customPageElementViewRendererFragment
					# eslint-disable-next-line relay/must-colocate-fragment-spreads
					...elementErrorBoundaryFragment
				}
				... on HelpLayoutVisualEntity {
					visualConfig {
						...alignmentSettingsContextFragment
						...alignmentSettingsContextHookFragment
					}
				}
			}
		`,
		subsection,
	);
	const subsectionId = subsectionData.__id;
	const alignmentSettings = useAlignmentSettings(subsectionData.visualConfig ?? null);
	const elements = useMemo(() => subsectionData.elements || [], [subsectionData.elements]);
	const isValidSpanOption = (n: number | string): n is SpanOptions => {
		return (typeof n === 'number' && n > 0 && n < 13) || n === 'none';
	};

	let span: SpanOptions = 12;
	if (subsectionData.config?.span && isValidSpanOption(subsectionData.config?.span)) {
		span = subsectionData.config?.span;
	}

	const horizontalAlignment = alignmentSettings.horizontalAlignment;
	return (
		<GridItem
			span={{
				sm: span,
			}}
			data-testid="layout-subsection"
		>
			<SubsectionChildWrapper id={subsectionData.__id}>
				{elements && elements.length > 0 && (
					<CoveredStack horizontalAlignment={horizontalAlignment}>
						{elements.map(
							(element) =>
								element &&
								(isElementRendererSplitEnabled() && ElementRenderer ? (
									<ElementErrorBoundary
										screenName={ScreenName.VIEW_HOMEPAGE}
										helpLayoutElement={element}
										key={element.__id}
									>
										<ElementRenderer
											key={element.__id}
											element={element}
											parentSubsectionId={subsectionId}
										/>
									</ElementErrorBoundary>
								) : (
									<ElementErrorBoundary
										screenName={ScreenName.VIEW_HOMEPAGE}
										key={element.__id}
										helpLayoutElement={element}
									>
										<HomePageElementViewRenderer
											key={element.__id}
											element={element}
											parentSubsectionId={subsectionId}
										/>
									</ElementErrorBoundary>
								)),
						)}
					</CoveredStack>
				)}
			</SubsectionChildWrapper>
		</GridItem>
	);
};

export default SubsectionViewRenderer;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const SubsectionChildWrapper = styled.div({
	height: '100%',
	width: '100%',
	boxSizing: 'border-box',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const CoveredStack = styled.div<AlignmentSettingsType>({
	height: '100%',
	display: 'flex',
	boxSizing: 'border-box',
	flexDirection: 'column',
	gap: token('space.100', '8px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Disabled to rollout go/ui-styling-standard tooling, please resolve
	textAlign: (props) => (props.horizontalAlignment ?? 'left').toLowerCase(),
});
